import { DatePicker, ConfigProvider } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

/**
 * AppDatePicker — a clean, on-brand calendar date picker built on Ant Design's
 * DatePicker.
 *
 * Why antd: it self-styles via CSS-in-JS (no global theme stylesheet to import,
 * unlike PrimeReact) and ships a polished calendar out of the box. The older
 * flatpickr-based DatePicker caused issues, so this is a deliberate new component.
 *
 * Exposes a simple controlled string interface in 'YYYY-MM-DD' (what the backend
 * stores) while displaying dd-mm-yyyy, converting to/from a dayjs value internally.
 */
type Props = {
  id?: string;
  /** Controlled value in 'YYYY-MM-DD' format ('' when empty). */
  value?: string;
  /** Called with the new 'YYYY-MM-DD' value ('' when cleared). */
  onChange: (value: string) => void;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  invalid?: boolean;
  disabled?: boolean;
};

export default function AppDatePicker({
  id,
  value,
  onChange,
  placeholder = 'Select date',
  minDate,
  maxDate,
  invalid,
  disabled,
}: Props) {
  const parsed = value ? dayjs(value) : null; // 'YYYY-MM-DD' is ISO, parses natively
  const current = parsed && parsed.isValid() ? parsed : null;

  const disabledDate = (date: Dayjs) => {
    if (!date) return false;
    if (minDate && date.isBefore(dayjs(minDate), 'day')) return true;
    if (maxDate && date.isAfter(dayjs(maxDate), 'day')) return true;
    return false;
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#0d9488', // teal-600 — on brand
          borderRadius: 8,
          controlHeight: 44,
          fontSize: 14,
        },
      }}
    >
      <DatePicker
        id={id}
        value={current}
        onChange={(d) => onChange(d ? d.format('YYYY-MM-DD') : '')}
        placeholder={placeholder}
        format="DD-MM-YYYY"
        disabledDate={disabledDate}
        disabled={disabled}
        status={invalid ? 'error' : undefined}
        allowClear
        style={{ width: '100%' }}
      />
    </ConfigProvider>
  );
}
